<script lang="ts" setup>
  import {
    DocumentTemplatesService,
    DocumentsService,
    type TDocumentTemplate,
    type TDocument,
    ImagePresetEnum,
    type ImageproxyImagePreset
  } from '@/types/generated'
  import { computed, onMounted, ref } from 'vue'

  const props = defineProps({
    templateUuid: {
      type: String,
      required: true
    },
    documentUuid: {
      type: String,
      required: false
    }
  })

  const document = ref<TDocument>(null)
  const template = ref<TDocumentTemplate>(null)
  const loading = ref(true)

  onMounted(async () => {
    try {
      loading.value = true
      template.value = await DocumentTemplatesService.documentTemplatesRetrieve({
        uuid: props.templateUuid
      })

      if (props.documentUuid) {
        document.value = await DocumentsService.documentsRetrieve({
          uuid: props.documentUuid
        })
      }
    } catch (error) {
      console.error('Error fetching document:', error)
    } finally {
      loading.value = false
    }
  })

  const documentPreview = computed<ImageproxyImagePreset | null>(() => {
    if (!document.value) return null
    return document.value.images.find((image) => image.image_preset === ImagePresetEnum.LARGE)
  })

  const templatePreview = computed<ImageproxyImagePreset | null>(() => {
    if (!template.value) return null
    return template.value.document.images.find(
      (image) => image.image_preset === ImagePresetEnum.LARGE
    )
  })
</script>
<template>
  <div>
    <div
      v-if="loading"
      class="flex items-center justify-center py-8"
    >
      <div class="loader"></div>
    </div>
    <div
      v-else
      class="document-comparison-container"
    >
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- Template Document -->
        <div class="document-panel rounded border p-4 shadow-sm">
          <h3 class="mb-4 text-lg font-semibold">Template</h3>
          <div
            v-if="template"
            class="document-content"
          >
            <img
              :src="templatePreview?.url"
              alt="Template Preview"
              class="mb-2"
            />
          </div>
        </div>

        <!-- Generated Document -->
        <div class="document-panel rounded border p-4 shadow-sm">
          <h3 class="mb-4 text-lg font-semibold">Generated Document</h3>
          <div
            v-if="document"
            class="document-content"
          >
            <img
              :src="documentPreview?.url"
              alt="Template Preview"
              class="mb-2"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .document-comparison-container {
    max-width: 100%;
    overflow-x: hidden;
  }

  .document-panel {
    max-height: 80vh;
    overflow-y: auto;
    background-color: white;
  }

  .document-content {
    min-height: 300px;
  }

  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
